// Pure presentation model for the review screen (`client-rounds-review`): the mm:ss
// transport clock, the tick↔track-fraction mapping the draggable seek thumb needs, the
// speed steps (including the ½× step the live transport deliberately lacks), the screen
// identity line, the score readout's stat rows, and the tier-indicator state registry.
//
// No React, no DOM — every rule here is unit-testable on its own, and the components
// below it stay thin.

import type { EvictedSpan } from '../../net/rounds-trace';
import type { ReviewRun } from '../../net/rounds-review-source';

// --- the transport ---------------------------------------------------------------

/**
 * The offered speed steps. **½× is the headline difference from the live replay
 * transport**, which starts at 1× and only speeds up: scrubbing a fast, already-
 * simulated batch trace for diagnosis wants slow-motion more than fast-forward.
 */
export const REVIEW_SPEED_STEPS = [0.5, 1, 4, 16] as const;

export type ReviewSpeed = (typeof REVIEW_SPEED_STEPS)[number];

/** The default step: ordinary speed, with ½× one tap away. */
export const DEFAULT_REVIEW_SPEED: ReviewSpeed = 1;

/** A speed step's label. ½× renders as the vulgar fraction, not "0.5×". */
export function speedLabel(step: number): string {
  return step === 0.5 ? '½×' : `${step}×`;
}

/** mm:ss for a tick, at the trace's tick rate. Zero-padded on both fields so the clock
 * never jitters in width. */
export function clockLabel(tick: number, tickRateHz: number): string {
  const seconds = tickRateHz > 0 ? Math.floor(Math.max(0, tick) / tickRateHz) : 0;
  const mm = Math.floor(seconds / 60);
  const ss = seconds % 60;
  return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
}

/** The transport's "position / total" clock, total computed from the terminal tick. */
export function transportClock(position: number, terminalTick: number, tickRateHz: number): string {
  return `${clockLabel(position, tickRateHz)} / ${clockLabel(terminalTick, tickRateHz)}`;
}

/** Where on the scrub track a tick sits, as a 0..1 fraction. A degenerate span pins to
 * the start rather than dividing by zero. */
export function fractionAtTick(tick: number, firstTick: number, terminalTick: number): number {
  const span = terminalTick - firstTick;
  if (span <= 0) {
    return 0;
  }
  return Math.min(1, Math.max(0, (tick - firstTick) / span));
}

/** The tick a point on the scrub track names — the drag-to-seek target. Any point on
 * the track is reachable (**A4**: a `tick → byte offset` index makes it an O(1) range
 * read), which is precisely what the live transport's read-only strip cannot offer. */
export function tickAtFraction(fraction: number, firstTick: number, terminalTick: number): number {
  const clamped = Math.min(1, Math.max(0, Number.isFinite(fraction) ? fraction : 0));
  return Math.round(firstTick + clamped * (terminalTick - firstTick));
}

// --- the tier indicator -----------------------------------------------------------

/**
 * The **three** tier states the review screen can be in: Detail (the close-zoom tick
 * range is resident), Coarse (zoomed out, scrubbing faster than ranges can be fetched, or
 * simply not there yet — the normal wide view, identical to the live Match screen's wide
 * LOD tier), and Evicted (the trace's detail identity has been reclaimed by retention, so
 * detail for that position will **never** arrive — `rounds-trace-retention`).
 *
 * Evicted was staged for exactly this landing by `rounds-slice` (artifacts-review **F6**,
 * **D6**): `TierIndicator` renders an arbitrary `TierStateDescriptor` rather than
 * switching on a closed union, so the third row below is the whole of the work — no
 * rework of the component was required.
 */
export type TierStateId = 'detail' | 'coarse' | 'evicted';

/**
 * Everything the indicator needs to render a state. **Chrome is not part of the
 * descriptor**: every state gets the same neutral tokens from the component, so no
 * future state can be accidentally colour-privileged or coloured `--danger`. States
 * differ only in their word and their edge.
 */
export interface TierStateDescriptor {
  id: string;
  /** The word in the pill. */
  label: string;
  /** The pill's `title` — why this tier, in plain language, never as an apology. */
  hint: string;
  /**
   * The pill's border treatment. `dashed` marks a state whose detail will never arrive,
   * so waiting would not help — today that is `evicted` alone; `detail` and `coarse` are
   * both `solid` because each of them is a view that is either loaded or still coming.
   */
  edge: 'solid' | 'dashed';
}

/** The reachable states, by id. */
export const TIER_STATES: Record<TierStateId, TierStateDescriptor> = {
  // NOT accent- or `--info`-coloured, deliberately (**D6**): colouring the healthy state
  // trains the eye to read the neutral one as degraded, which is the wrong lesson when
  // coarse is a legitimate way to watch a match.
  detail: {
    id: 'detail',
    label: 'Detail',
    hint: 'Close detail for this tick is loaded — individual bees.',
    edge: 'solid',
  },
  coarse: {
    id: 'coarse',
    label: 'Coarse',
    hint: 'The wide view — the same tier a live match shows. Nothing is wrong.',
    edge: 'solid',
  },
  // The `dashed` edge `TierStateDescriptor` reserved for exactly this: a range whose
  // detail will never arrive, so waiting for it would be pointless. Still neutral chrome
  // and never `--danger` (**D6**) — the bytes were deliberately reclaimed, not damaged,
  // the round is still fully watchable at coarse, and its score is unaffected.
  evicted: {
    id: 'evicted',
    label: 'Coarse only',
    hint: "This span's detail was reclaimed to make room for newer rounds — the round stays watchable at coarse, and waiting will not bring it back.",
    edge: 'dashed',
  },
};

/** The current tier for a position: Detail when its record is resident, Evicted when the
 * position falls inside a reclaimed span and never will be resident, Coarse otherwise —
 * a range still in flight is Coarse, a real view, not a placeholder. Residency wins if
 * both are somehow asserted at once: a resident range is never reported unreachable. */
export function tierStateFor(detailResident: boolean, detailEvicted: boolean): TierStateDescriptor {
  if (detailResident) {
    return TIER_STATES.detail;
  }
  return detailEvicted ? TIER_STATES.evicted : TIER_STATES.coarse;
}

/** Whether `tick` falls inside any of `spans` (inclusive at both ends) — the one signal
 * the tier indicator and the scrub track both read, so they cannot disagree
 * (`client-rounds-review`). */
export function tickInsideSpans(tick: number, spans: readonly EvictedSpan[]): boolean {
  return spans.some((span) => tick >= span.fromTick && tick <= span.toTick);
}

// --- screen identity + score readout ----------------------------------------------

/** The run kind, spelled for a human. Sandbox is the only kind this slice produces. */
export function runKindLabel(kind: ReviewRun['kind']): string {
  return kind === 'sandbox' ? 'Sandbox run' : kind;
}

/** Digit-grouped with spaces, matching the sketch's `seed 77 314`. */
export function groupDigits(value: number): string {
  return Math.trunc(value)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

/** The persistent identity line: what is being reviewed, unambiguously. A player must
 * never be left scrubbing a trace whose provenance is unclear — a sandbox run's seed and
 * solo composition are exactly what make its score non-comparable to a graded round's. */
export function runIdentityLine(run: ReviewRun): string {
  const colonies = `${run.colonyCount} ${run.colonyCount === 1 ? 'colony' : 'colonies'}`;
  return `seed ${groupDigits(run.seed)} · ${colonies} · ${clockLabel(run.terminalTick, run.tickRateHz)}`;
}

/** One row of the score readout's supporting evidence. */
export interface ScoreStatRow {
  label: string;
  value: string;
}

/**
 * The supporting stats beside the G1 score. Every label describes **behaviour** — none
 * describes standing, because a sandbox score is not comparable to a graded one.
 */
export function scoreStatRows(run: ReviewRun): ScoreStatRow[] {
  return [
    {
      label: 'Peak population',
      // Not recorded by the surface's run list — said plainly rather than substituting the final
      // population, which would read as a peak while being a different number.
      value:
        run.stats.peakPopulation === null ? 'not recorded' : groupDigits(run.stats.peakPopulation),
    },
    { label: 'Nectar stored', value: groupDigits(run.stats.nectarStored) },
    {
      label: 'Colony ended',
      value:
        run.stats.eliminationTick === null
          ? 'survived the run'
          : `tick ${groupDigits(run.stats.eliminationTick)}`,
    },
  ];
}

/** The G1 metric's unit, spelled out so the number is never a bare score. */
export const SCORE_UNIT = 'bees alive at the end';

/** The score readout's heading. Framed as "how it went", not "how it placed". */
export const SCORE_HEADING = 'How this run went';

/**
 * The **R3** not-a-forecast caption. Required, not decorative: a sandbox run is the
 * player's colony alone (**G3**) on a seed they can see, while a graded round is
 * contested (**G4**) on a fresh, hidden seed (**G6**). A player who reads this number as
 * a forecast will feel cheated when the graded result differs.
 */
export const NOT_A_FORECAST_CAPTION =
  'A sandbox run is your colony alone, on a different seed from a graded round — so this is ' +
  'evidence about how your bees behave, not a predicted placing.';
